
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Customer } from '@/lib/supabase/customers/types';

interface CustomerData {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  country?: string;
  region?: string;
  municipality?: string;
  address?: string;
  organization_id?: string;
  shop_id?: string;
}

export const useSaveCustomer = (onSuccess: () => void) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { toast } = useToast();
  
  // Update a customer
  const updateCustomer = async (customerId: string, data: Partial<CustomerData>) => {
    if (!customerId) return null;
    
    try {
      setSaving(true);
      setErrors({});
      
      // Validate required fields
      if (!data.name || !data.name.trim()) {
        setErrors({ name: 'Le nom est obligatoire' });
        return null;
      }
      
      if (!data.phone || !data.phone.trim()) {
        setErrors({ phone: 'Le numéro de téléphone est obligatoire' });
        return null;
      }
      
      if (customerId === 'new') {
        // Get organization ID from localStorage
        const organizationId = localStorage.getItem('currentOrganizationId');
        
        if (!organizationId) {
          throw new Error('Organization ID not found');
        }
        
        // Check if a customer with this phone number already exists in the same shop
        if (data.phone && data.shop_id) {
          console.log("Checking for duplicate phone number:", data.phone, "in shop:", data.shop_id);
          const { data: existingCustomers } = await supabase
            .from('customers')
            .select('id, name')
            .eq('phone', data.phone)
            .eq('shop_id', data.shop_id);
            
          if (existingCustomers && existingCustomers.length > 0) {
            console.log("Found existing customer with same phone number:", existingCustomers[0]);
            setErrors({ phone: `Un client avec ce numéro existe déjà dans cette boutique (${existingCustomers[0].name})` });
            return null;
          }
        }
        
        // Create a new customer
        const { data: newCustomer, error } = await supabase
          .from('customers')
          .insert({
            ...data,
            organization_id: organizationId
          })
          .select('*')
          .single();
          
        if (error) {
          if (error.code === '23505') {
            setErrors({ phone: 'Un client avec ce numéro existe déjà dans cette boutique' });
            return null;
          }
          throw error;
        }
        
        toast({
          title: 'Client créé',
          description: "Le nouveau client a été créé avec succès",
        });
        
        onSuccess();
        return newCustomer;
      } else {
        // For existing customer, check if phone is changing and if it would conflict
        if (data.phone && data.shop_id) {
          const { data: currentCustomer } = await supabase
            .from('customers')
            .select('phone, shop_id')
            .eq('id', customerId)
            .single();
            
          // Only check for conflicts if phone or shop_id is changing
          if (currentCustomer && (data.phone !== currentCustomer.phone || 
              (data.shop_id !== currentCustomer.shop_id && data.shop_id))) {
            
            const { data: existingCustomers } = await supabase
              .from('customers')
              .select('id, name')
              .eq('phone', data.phone)
              .eq('shop_id', data.shop_id || currentCustomer.shop_id)
              .neq('id', customerId);
              
            if (existingCustomers && existingCustomers.length > 0) {
              setErrors({ phone: `Un client avec ce numéro existe déjà dans cette boutique (${existingCustomers[0].name})` });
              return null;
            }
          }
        }
        
        // Update existing customer
        const { data: updatedCustomer, error } = await supabase
          .from('customers')
          .update(data)
          .eq('id', customerId)
          .select('*')
          .single();
          
        if (error) {
          if (error.code === '23505') {
            setErrors({ phone: 'Un client avec ce numéro existe déjà dans cette boutique' });
            return null;
          }
          throw error;
        }
        
        toast({
          title: 'Client mis à jour',
          description: "Les informations du client ont été mises à jour avec succès",
        });
        
        onSuccess();
        return updatedCustomer;
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      
      if (error && typeof error === 'object' && 'code' in error && error.code === '23505') {
        setErrors({ phone: 'Un client avec ce numéro existe déjà dans cette boutique' });
      } else {
        toast({
          title: 'Erreur',
          description: "Impossible de mettre à jour le client",
          variant: 'destructive',
        });
      }
      
      return null;
    } finally {
      setSaving(false);
    }
  };
  
  return {
    saving,
    errors,
    updateCustomer,
  };
};

// For backwards compatibility
export const useSaveClient = useSaveCustomer;
